/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import LegalPageLayout from './LegalPageLayout';
import { Badge } from '@/components/ui/badge';
import { LEGAL_OWNER, ADDON_MODULES_EXCLUDED } from '@/lib/legalOwner';

const TermsOfServicePage = () => (
  <LegalPageLayout
    activeKey="terms"
    title="Terms of Service"
    subtitle="Condizioni Generali di Licenza del software SanitàNexus (Core Architecture)."
    lastUpdated={LEGAL_OWNER.lastReview}
  >
    <div className="rounded-lg border-l-4 border-red-500 bg-red-500/10 p-4 not-prose mb-6">
      <div className="font-bold text-red-800 dark:text-red-300 uppercase tracking-wider text-xs mb-1">
        Clausola essenziale — leggere prima di procedere
      </div>
      <p className="text-sm text-red-900 dark:text-red-100 leading-relaxed">
        Il software SanitàNexus è fornito <strong>&ldquo;as-is&rdquo;</strong> — così com'è.
        L'acquirente (clinica o professionista sanitario) è l'<strong>unico Titolare del Trattamento</strong>
        {' '}di qualsiasi dato sanitario (PHI) processato tramite un'installazione del software. Il licenziante
        <strong> non presta alcuna garanzia</strong>, esplicita o implicita, di idoneità a un uso clinico
        specifico e <strong>declina espressamente</strong> ogni responsabilità per danni diretti, indiretti,
        consequenziali o punitivi derivanti dall'utilizzo del software.
      </p>
    </div>

    <h2>1. Parti</h2>
    <p>
      Le presenti condizioni regolano il rapporto tra:
    </p>
    <ul>
      <li>
        <strong>Licenziante</strong>: {LEGAL_OWNER.fullName}, persona fisica, Codice Fiscale
        {' '}<code>{LEGAL_OWNER.fiscalCode}</code>, con domicilio fiscale in {LEGAL_OWNER.addressLine}
        {' '}(di seguito il &ldquo;<em>Licenziante</em>&rdquo;).
      </li>
      <li>
        <strong>Licenziatario</strong>: la persona fisica o giuridica che scarica, installa o utilizza
        il software (di seguito il &ldquo;<em>Cliente</em>&rdquo; o &ldquo;<em>Buyer</em>&rdquo;).
      </li>
    </ul>
    <p>
      <em>{LEGAL_OWNER.vatExemptionNotice}</em>
    </p>

    <h2>2. Oggetto della licenza — Core Architecture</h2>
    <p>
      La presente licenza standard concede al Cliente il diritto <strong>non esclusivo e non trasferibile</strong>
      {' '}di installare e utilizzare la sola <strong>Core Architecture</strong> di SanitàNexus, che comprende:
    </p>
    <ul>
      <li>Anagrafica pazienti e gestione appuntamenti;</li>
      <li>Cartella clinica elettronica di base (note SOAP, diagnosi, prescrizioni);</li>
      <li>Modulo videoconsulto (WebRTC);</li>
      <li>Fatturazione elettronica FatturaPA e ricevute PDF;</li>
      <li>Impostazioni di branding e configurazione ISEE;</li>
      <li>Aggiornamenti di sicurezza per 12 mesi dalla data di acquisto.</li>
    </ul>

    <h2 id="excluded-addons">3. Moduli Add-on esclusi dalla licenza standard</h2>
    <p>
      <strong>Sono espressamente esclusi</strong> dalla presente licenza i seguenti moduli avanzati,
      che sono legalmente classificati come <em>Add-on opzionali</em> e disponibili unicamente tramite
      licenze standalone separate, il cui prezzo e le cui condizioni sono negoziati caso per caso:
    </p>
    <div className="not-prose grid gap-3 my-4">
      {ADDON_MODULES_EXCLUDED.map((m) => (
        <div key={m.id} className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-amber-700 dark:text-amber-300 border-amber-500/60">
              Escluso dalla Core
            </Badge>
            <span className="font-semibold text-sm">{m.name}</span>
          </div>
          <p className="text-xs text-muted-foreground leading-snug">{m.description}</p>
        </div>
      ))}
    </div>
    <p>
      Il Cliente riconosce e accetta che l'attivazione di uno qualsiasi dei moduli sopra elencati
      richiede l'acquisto di una licenza aggiuntiva; qualsiasi utilizzo non autorizzato di tali moduli
      costituisce violazione della presente licenza e giustifica la revoca immediata della stessa,
      fatto salvo il diritto del Licenziante al risarcimento del danno.
    </p>

    <h2>4. Local-First — Nessun accesso ai dati clinici</h2>
    <p>
      Il software è distribuito con architettura <strong>local-first</strong>. Il Licenziante fornisce
      esclusivamente il codice e non ha accesso ai server, ai database o alla rete su cui il software
      è installato. Il Cliente riconosce che, in questa configurazione, il Licenziante non è tecnicamente
      in grado di visualizzare, esportare, modificare o cancellare alcun dato dei pazienti.
    </p>

    <h2>5. Titolarità del Trattamento — Clausola di Trasferimento</h2>
    <p>
      Ai sensi dell'Art. 4(7) GDPR, il <strong>Cliente è l'unico Titolare del Trattamento</strong> di
      tutti i dati sanitari, identificativi e amministrativi processati dall'istanza installata.
      Rientra nella responsabilità esclusiva del Cliente:
    </p>
    <ul>
      <li>Redigere l'informativa privacy destinata ai pazienti;</li>
      <li>Raccogliere e conservare i consensi al trattamento;</li>
      <li>Nominare, ove necessario, un DPO ai sensi dell'Art. 37 GDPR;</li>
      <li>Tenere il registro dei trattamenti;</li>
      <li>Notificare eventuali data breach al Garante entro 72 ore;</li>
      <li>Adottare le misure di sicurezza adeguate ai sensi dell'Art. 32 GDPR.</li>
    </ul>

    <h2>6. Clausola di Assenza di Responsabilità (&ldquo;No-Liability&rdquo;)</h2>
    <p>
      Nella misura massima consentita dalla legge italiana, il Licenziante <strong>declina ogni responsabilità</strong>
      {' '}per:
    </p>
    <ol>
      <li>Errori clinici, diagnostici o terapeutici commessi da operatori sanitari che utilizzino il software;</li>
      <li>Perdita, danneggiamento o alterazione di dati imputabili a malfunzionamento dell'hardware, del
        sistema operativo o della rete del Cliente;</li>
      <li>Interruzioni di servizio dovute a fattori esterni (rete, energia, forza maggiore);</li>
      <li>Utilizzo non conforme del software rispetto alla documentazione fornita;</li>
      <li>Danni indiretti, mancato guadagno, perdita di chance, danno reputazionale.</li>
    </ol>
    <p>
      In ogni caso, l'eventuale responsabilità del Licenziante è <strong>limitata all'importo corrisposto
      dal Cliente</strong> per la licenza nei 12 mesi antecedenti l'evento che ha originato la pretesa
      risarcitoria.
    </p>

    <h2>7. Proprietà intellettuale</h2>
    <p>
      Il software è tutelato dalla Legge italiana n. 633/1941 sul Diritto d'Autore. La licenza non
      trasferisce al Cliente alcun diritto di proprietà intellettuale sul codice sorgente, sui marchi
      o sulla documentazione. È vietata la redistribuzione a terzi senza previo accordo scritto.
    </p>

    <h2>8. Corrispettivo e assenza di P.IVA</h2>
    <p>
      Il corrispettivo della licenza è definito nell'ordine di acquisto. Trattandosi di licenza fornita
      da persona fisica ai sensi dell'Art. 3, c. 4, lett. a, DPR 633/1972, il rapporto è
      <strong> esente dall'obbligo di applicazione dell'IVA</strong> e non prevede emissione di
      fattura elettronica al Sistema di Interscambio.
    </p>

    <h2>9. Durata, recesso, risoluzione</h2>
    <p>
      La licenza ha durata di 12 mesi rinnovabili. Il Cliente può recedere in qualsiasi momento senza
      diritto a rimborso delle mensilità già maturate. Il Licenziante può risolvere il contratto in
      caso di violazione delle clausole 3 (Add-on esclusi), 5 (Titolarità del Trattamento) o 7
      (Proprietà intellettuale).
    </p>

    <h2>10. Modifiche</h2>
    <p>
      Il Licenziante si riserva di aggiornare le presenti condizioni. Le modifiche entrano in vigore
      30 giorni dopo la pubblicazione su <code>/legal/terms</code>. Il Cliente che non accetti le
      nuove condizioni può recedere entro tale termine.
    </p>

    <h2>11. Legge applicabile e foro competente</h2>
    <p>
      Il presente contratto è regolato dalla legge italiana. Per ogni controversia è competente in via
      esclusiva il <strong>Foro di Pavia (PV)</strong>, con rinuncia espressa a qualsiasi foro alternativo.
    </p>

    <h2>12. Contatti</h2>
    <p>
      Per ogni comunicazione relativa alle presenti condizioni:
      <a href={`mailto:${LEGAL_OWNER.contactEmail}`}> {LEGAL_OWNER.contactEmail}</a>.
    </p>

    <h2>13. Accettazione</h2>
    <p>
      L'installazione e/o l'utilizzo del software costituiscono <strong>accettazione integrale</strong>
      {' '}delle presenti condizioni, ivi comprese le clausole di limitazione della responsabilità (§6)
      e di esclusione dei moduli Add-on (§3) che il Cliente dichiara di aver specificamente esaminato
      e approvato ai sensi degli Artt. 1341-1342 c.c.
    </p>
  </LegalPageLayout>
);

export default TermsOfServicePage;
